import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { saveProfile, useSaveAppearance } from "../../theme/accountAppearance";
import { PALETTES, type Palette, THEMES, type Theme, useTheme } from "../../theme/ThemeProvider";
import {
  Card,
  CardHeader,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  PageHeader,
} from "../../ui/components";
import { SkeletonCard, SkeletonPageHeader } from "../../ui/skeletons";

// The two languages the rest of the product is actually written for. A third offered here would be
// a language somebody could choose and then find nothing rendered in.
const LOCALES = [
  { id: "en-US", label: "English (US)" },
  { id: "en-GB", label: "English (UK)" },
] as const;

// The one page in the product that belongs to the person rather than to a workspace, which is why
// it asks for no permission at all: somebody holding no role on either half of the building still
// has a name, still reads in one language, and still signs their mail.
export function ProfilePage() {
  const auth = useAuth();
  const { theme, palette } = useTheme();
  const saveAppearance = useSaveAppearance();
  const profile = auth.session?.user;
  const [displayName, setDisplayName] = useState(profile?.displayName ?? "");
  const [locale, setLocale] = useState(profile?.locale ?? "en-US");
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatarUrl ?? "");
  const [signature, setSignature] = useState(profile?.signature ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState<unknown>();
  const [appearanceError, setAppearanceError] = useState<unknown>();

  async function saveDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(undefined);
    setSaved("");
    try {
      // The whole record every time, because the endpoint replaces rather than merges. The appearance
      // half of it is read from what is on screen rather than from the session, so saving a name
      // cannot quietly undo colors chosen a moment earlier and not yet echoed back.
      await saveProfile({
        displayName: displayName.trim(),
        locale,
        theme,
        palette,
        avatarUrl: avatarUrl.trim(),
        signature,
      });
      await auth.refresh();
      setSaved("Your details are saved.");
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  }

  // No save button, because there is nothing to confirm: the product is already drawn the way the
  // control says, and the only question left is whether the account was told. That question has an
  // answer here, which is why this page reports a failure the sidebar cannot.
  async function chooseAppearance(next: { theme?: Theme; palette?: Palette }) {
    setAppearanceError(undefined);
    try {
      await saveAppearance(next);
    } catch (caught) {
      setAppearanceError(caught);
    }
  }

  if (!profile)
    return (
      <>
        <SkeletonPageHeader />
        <div className="content-grid">
          <SkeletonCard className="span-7" />
          <SkeletonCard className="span-5" />
        </div>
      </>
    );

  return (
    <>
      <PageHeader
        title="Your profile"
        description="Your name, the language the product speaks to you in, how it is drawn, and the sign-off that goes out under your messages. All of it follows you onto any machine you sign in on."
      />
      <div className="content-grid">
        <Card className="span-7">
          <CardHeader
            title="Your details"
            description="What colleagues and customers see when this account acts."
          />
          {error ? <ErrorPanel error={error} /> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <form className="form-grid" onSubmit={saveDetails}>
            <Field label="Full name">
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                maxLength={120}
              />
            </Field>
            <Field
              label="Email address"
              hint="The address you sign in with. Changing it is an administrator's job, because it is how the product knows you."
            >
              <input value={profile.email} readOnly />
            </Field>
            <Field label="Language">
              <select value={locale} onChange={(event) => setLocale(event.target.value)}>
                {LOCALES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Picture address"
              hint="An https address the product can fetch. Leave it empty to show no picture."
            >
              <input
                value={avatarUrl}
                onChange={(event) => setAvatarUrl(event.target.value)}
                type="url"
                inputMode="url"
                maxLength={2048}
                placeholder="https://"
              />
            </Field>
            <div className="full">
              <Field
                label="Email signature"
                hint="Plain text, added under the messages you send. The line breaks you type are kept exactly as they are."
              >
                <textarea
                  value={signature}
                  onChange={(event) => setSignature(event.target.value)}
                  rows={4}
                  maxLength={2000}
                />
              </Field>
            </div>
            <div className="full">
              <FormActions submitting={saving} submitLabel="Save details" />
            </div>
          </form>
        </Card>
        <Card className="span-5">
          <CardHeader
            title="Appearance"
            description="Kept with your account rather than with this browser, so the product looks the same on the next machine you sign in on. Saved the moment you choose."
          />
          {appearanceError ? <ErrorPanel error={appearanceError} /> : null}
          <div className="form-stack">
            <Field
              label="Appearance"
              hint="Light unless you say otherwise, whatever this device happens to prefer."
            >
              <select
                value={theme}
                onChange={(event) => chooseAppearance({ theme: event.target.value as Theme })}
              >
                {THEMES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Color theme" hint="Which set of colors light and dark are drawn from.">
              <select
                value={palette}
                onChange={(event) => chooseAppearance({ palette: event.target.value as Palette })}
              >
                {PALETTES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Card>
        <Card className="span-12">
          <CardHeader
            title="Sign-in and security"
            description="Your password, two-step verification and the sessions your account is signed in on are kept apart from the rest of your profile, because losing one of them costs more than a mistyped name."
          />
          <Link className="button secondary" to="/security">
            Open account security
          </Link>
        </Card>
      </div>
    </>
  );
}
